import React from 'react';
import { Table, TableContainer, Box, Paper, CircularProgress, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

import RenderIf from '../../renderIf';
import useRequestBuilder from './useRequestBuilder';
import { BiColorHeader } from '../scholarshipApplication/components';
import { FilterControls, Search, DashboardHeader, DashboardBody } from './components';
import ShipyardMenuButton from '../../shared/shipyardMenu';
import { MuiTablePagination } from '../../shared/muiControls';

const AdminDashboard = () => {
    const { state: savedState } = useLocation();
    const {
        page,
        rowsPerPage,
        filters,
        selectedFilters,
        list,
        headers,
        // hasMore, #TODO If the full dataset retrieval ever causes problems in terms of Lambda timeouts, this may need to come back.
        isInitialized,
        totalRecordCount,
        totalUnfilteredCount,
        sortKey,
        sortDir,
        isFetching,
        searchValue,
        searchHelperText,
        reportDownloadURL,
        handleFilterString,
        handleSort,
        handleNav,
        handlePageChange,
        handleRowsPerPageChange,
        retrieveList,
        // loadMore, #TODO If the full dataset retrieval ever causes problems in terms of Lambda timeouts, this may need to come back.
        exportApplications,
    } = useRequestBuilder(savedState);

    const [isCompressed, setIsCompressed] = React.useState(false);

    const tableRef = React.useRef();
    const reportDownloadRef = React.useRef();

    React.useEffect(() => {
        retrieveList();
    }, []); //Mount

    return (
        <Box
            sx={{
                marginTop: (theme) => theme.spacing(1),
                paddingRight: (theme) => theme.spacing(3),
                paddingLeft: (theme) => theme.spacing(3),
            }}
        >
            {/* TODO TechDebt Replace when we can verify this works for all pages and all browser types. */}
            {/* <GlobalStyles styles={{
                '&::-webkit-scrollbar': {
                    width: '0.5rem',
                },
                '&::-webkit-scrollbar-track': {
                    border: 'solid 3px transparetn',
                    boxShadow: 'inset 0 0 10px 10px transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#014E8D',
                    borderRadius: '8px',
        
                    '&:hover': {
                        backgroundColor: '#0276d6',
                    }
                }
            }} /> */}

            <BiColorHeader
                left="My"
                right="Applications."
            />

            <RenderIf isTrue={isInitialized && filters instanceof Object}>
                <FilterControls
                    isFetching={isFetching}
                    filters={filters}
                    currentCount={list.length}
                    totalRecordCount={totalRecordCount}
                    handleFilterChange={handleFilterString}
                    selectedFilters={selectedFilters}
                />
            </RenderIf>

            <Paper
                sx={theme=> {
                    return {
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                        padding: theme.spacing(3),
                        borderRadius: '8px',
                        [theme.breakpoints.down('lg')]: {
                            maxWidth: '75vw',
                        },
                        [theme.breakpoints.down('md')]: {
                            maxWidth: '60vw',
                        },
                        [theme.breakpoints.down('sm')]: {
                            maxWidth: 'unset',
                        },
                    }
                }}
            >
                <RenderIf isTrue={isInitialized}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'right',
                        }}
                    >
                        <a
                            href={reportDownloadURL}
                            style={{ display: 'none' }}
                            ref={reportDownloadRef}
                        >
                            Export Download
                        </a>
                        <ShipyardMenuButton
                            label="Select Action"
                            actions={[
                                {
                                    label: 'Export all applications',
                                    action: async () => {
                                        let succeeded = await exportApplications();
                                        if (succeeded) {
                                            reportDownloadRef.current.click();
                                        }
                                    },
                                },
                            ]}
                            disabled={isFetching}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            variant="contained"
                        />
                    </Box>

                    <Search
                        disabled={isFetching}
                        searchText={searchHelperText}
                        searchValue={searchValue}
                        handleSearchChange={handleFilterString}
                        handleSwitch={() => setIsCompressed((prev) => !prev)}
                    />

                    <TableContainer
                        ref={tableRef}
                        sx={theme=> {
                            return {
                                maxHeight: '50vh',
                                overflow: 'auto',
                                [theme.breakpoints.down('sm')]: {
                                    maxHeight: '45vh',
                                },
                            }
                        }}
                    >
                        <Table
                            stickyHeader
                            size={isCompressed ? 'small' : 'medium'}
                        >
                            <DashboardHeader
                                disabled={isFetching}
                                headers={headers}
                                handleSort={handleSort}
                                sortKey={sortKey}
                                sortDir={sortDir}
                            />

                            <DashboardBody
                                list={list?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                                headers={headers}
                                isFetching={isFetching}
                                handleNav={handleNav}
                            />
                        </Table>
                    </TableContainer>

                    <MuiTablePagination
                        disabled={isFetching}
                        count={totalRecordCount || 0}
                        totalCount={totalUnfilteredCount || 0}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        showFirstButton={true}
                        showLastButton={true}
                    />
                </RenderIf>

                <RenderIf isTrue={!isInitialized}>
                    <Typography component="div">
                        <CircularProgress sx={{ marginRight: '1rem' }} /> Retrieving application list...
                    </Typography>
                </RenderIf>
            </Paper>
        </Box>
    );
};

export default AdminDashboard;
